import { useRef, useState, useEffect } from "react";
import { Tooltip } from "antd";
import { useMobileViewport } from "hooks/useMobileViewport";

/**
 * Walks every descendant element of the parent DOM node to define overflow status
 * This is required for structures like Antd Button where the icon <span class="ant-btn-icon">
 * can be the first child and the real text element is a sibling of it
 * Returns true as soon as any element is flagged for clipping (scrollWidth > clientWidth)
 */
function computeOverflowStatus(element) {
  if (!element) return false;

  const stack = [element];
  while (stack.length) {
    const currentElement = stack.pop();
    if (currentElement.scrollWidth > currentElement.clientWidth) return true;
    for (let i = currentElement.children.length - 1; i >= 0; i -= 1) {
      stack.push(currentElement.children[i]);
    }
  }

  return false;
};

const DynamicTooltip = ({ title, className, onClick, children, ...tooltipProps }) => {
  const { isMobileWidth } = useMobileViewport();
  const parentContainerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const parentContainer = parentContainerRef.current;
    if (!parentContainer) return;

    const updateOverflowStatus = () => setIsOverflowing(computeOverflowStatus(parentContainer));
    updateOverflowStatus();
    
    const ro = new ResizeObserver(updateOverflowStatus);
    ro.observe(parentContainer);
    return () => ro.disconnect();
  }, [isMobileWidth]);

  useEffect(() => {
    if (parentContainerRef.current) setIsOverflowing(computeOverflowStatus(parentContainerRef.current));
  }, [title, isMobileWidth]);

  if (isMobileWidth) {
    return (
      <div className={className} onClick={onClick} ref={parentContainerRef}>
        {children}
      </div>
    );
  }

  return (
    <Tooltip title={isOverflowing ? title : false} destroyTooltipOnHide {...tooltipProps}>
      <div className={className} onClick={onClick} ref={parentContainerRef}>
        {children}
      </div>
    </Tooltip>
  );
};

export default DynamicTooltip;